const S = {
  wrapper: {
    marginTop: '10px', padding: '40px', border: '3px solid #1a5c2a',
    fontFamily: "'Georgia', serif", color: '#0a1f0e', lineHeight: '1.7',
    background: '#f2faf4',
  },
  h1: {
    fontSize: '2rem', fontWeight: '700', borderBottom: '3px solid #2e8b47',
    paddingBottom: '8px', marginBottom: '20px', marginTop: '32px', color: '#145a24',
  },
  subtitle: {
    fontSize: '0.95rem', color: '#3a7a4a', marginTop: '16px', marginBottom: '28px', fontStyle: 'italic',
  },
  h2: {
    fontSize: '1.3rem', fontWeight: '700', color: '#1e7a36', marginTop: '36px',
    marginBottom: '12px', borderLeft: '4px solid #2e8b47', paddingLeft: '10px',
  },
  h3: {
    fontSize: '1.05rem', fontWeight: '700', color: '#2a6a3a', marginTop: '20px',
    marginBottom: '8px',
  },
  p: { marginBottom: '12px', fontSize: '0.97rem' },
  infoBox: {
    background: '#e8f5ec', border: '2px solid #2e8b47', borderRadius: '6px',
    padding: '16px 20px', margin: '16px 0',
  },
  infoBoxTitle: {
    fontWeight: '700', fontFamily: "'Courier New', monospace", fontSize: '0.88rem',
    color: '#145a24', marginBottom: '8px',
  },
  infoBoxItem: {
    fontSize: '0.9rem', margin: '4px 0', paddingLeft: '12px',
    borderLeft: '2px solid #2e8b47',
  },
  legendBox: {
    display: 'flex', flexWrap: 'wrap', gap: '12px', margin: '16px 0 20px 0',
    background: '#dff0e4', border: '1px solid #7abf8a', borderRadius: '6px',
    padding: '12px 16px',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' },
  legendDot: { width: '14px', height: '14px', borderRadius: '3px', flexShrink: 0 },
  noteBox: {
    background: '#fff9e6', border: '1px solid #c8a800', borderRadius: '5px',
    padding: '10px 14px', margin: '12px 0', fontSize: '0.88rem', color: '#5a4500',
  },
};

// ── Color coding for compatibility ratings ──────────────────────────────────
const RATING_COLORS = {
  'Excellent':    { bg: '#c6efce', text: '#1a5c1a', border: '#4caf50' },
  'Good':         { bg: '#d4edda', text: '#1c5a2a', border: '#6abf7a' },
  'Moderate':     { bg: '#fff3cd', text: '#6a4a00', border: '#ffc107' },
  'Limited':      { bg: '#fde2b8', text: '#7a3500', border: '#ff9800' },
  'Poor':         { bg: '#f8d7da', text: '#7a0010', border: '#f44336' },
  'N/A':          { bg: '#e9ecef', text: '#555', border: '#aaa' },
  'Yes':          { bg: '#c6efce', text: '#1a5c1a', border: '#4caf50' },
  'No':           { bg: '#f8d7da', text: '#7a0010', border: '#f44336' },
  'Conditional':  { bg: '#fff3cd', text: '#6a4a00', border: '#ffc107' },
  'With Coating': { bg: '#d4edda', text: '#1c5a2a', border: '#6abf7a' },
};

function RatingBadge({ value }) {
  // match the base key
  const key = Object.keys(RATING_COLORS).find(k => value && value.startsWith(k));
  const style = key ? RATING_COLORS[key] : { bg: '#e9ecef', text: '#444', border: '#bbb' };
  return (
    <span style={{
      background: style.bg, color: style.text, border: `1px solid ${style.border}`,
      borderRadius: '4px', padding: '2px 7px', fontSize: '0.82rem', fontWeight: '600',
      whiteSpace: 'nowrap', display: 'inline-block',
    }}>{value}</span>
  );
}

// ── Reusable two-column table ───────────────────────────────────────────────
function Table2({ rows }) {
  const thStyle = {
    background: '#1e7a36', color: '#fff', padding: '10px 14px',
    textAlign: 'left', fontWeight: '700', fontSize: '0.9rem',
  };
  const tdBase = {
    padding: '10px 14px', borderBottom: '1px solid #b8dfc2', verticalAlign: 'top',
    fontSize: '0.9rem', fontFamily: "'Courier New', monospace", whiteSpace: 'pre-wrap',
  };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
      <thead><tr>
        <th style={{ ...thStyle, width: '240px' }}>Property</th>
        <th style={thStyle}>Detail</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontWeight: '600', color: '#145a24', fontFamily: "'Georgia', serif" }}>{label}</td>
            <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7' }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

// ── Main materials data ─────────────────────────────────────────────────────
// Sources: ASM Aerospace Specification Metals, Matweb, ASTM standards,
//          FDA guidance, ISO 10993 family, manufacturer datasheets.
const MATERIALS = [
  // ── METALS ──
  {
    name: 'Aluminum 6061-T6',
    category: 'Metal',
    yieldMPa: '276',
    ultimateMPa: '310',
    shearMPa: '207',
    hardness: '95 HB',
    density: '2.70 g/cm³',
    h2o2Compat: 'Moderate',
    h2o2Note: 'Surface oxidation; anodizing improves resistance',
    gammaCap: 'Excellent',
    gammaNote: 'No degradation; used in radiation-therapy jigs',
    autoclave: 'No',
    autoclaveNote: '121 °C steam causes oxide layer damage; not recommended',
    biocompat: 'ISO 10993 tested; anodized grades widely used',
    medUse: 'Surgical instrument handles, imaging device frames, OR fixtures',
  },
  {
    name: 'Aluminum 6082-T6',
    category: 'Metal',
    yieldMPa: '250–260',
    ultimateMPa: '290–340',
    shearMPa: '194–227',
    hardness: '91–100 HB',
    density: '2.70 g/cm³',
    h2o2Compat: 'Moderate',
    h2o2Note: 'Similar to 6061; anodising improves H₂O₂ resistance; bare surface susceptible to pitting',
    gammaCap: 'Excellent',
    gammaNote: 'No degradation; used in radiation therapy and imaging fixtures',
    autoclave: 'No',
    autoclaveNote: '121 °C steam damages oxide layer; anodised coating may degrade over repeat cycles',
    biocompat: 'ISO 10993 tested for anodised grades; non-implant use only',
    medUse: 'Structural frames, imaging device housings, OR table components, jigs and fixtures',
  },
  {
    name: '304 Stainless Steel',
    category: 'Metal',
    yieldMPa: '215',
    ultimateMPa: '505',
    shearMPa: '386',
    hardness: '80 HRB',
    density: '8.00 g/cm³',
    h2o2Compat: 'Good',
    h2o2Note: 'Resistant to dilute H₂O₂; concentrated >30% may pit over time',
    gammaCap: 'Excellent',
    gammaNote: 'No degradation; standard in radiotherapy equipment',
    autoclave: 'Yes',
    autoclaveNote: 'Standard 121 °C / 134 °C cycles; widely used for instruments',
    biocompat: 'ISO 5832-1; widely accepted for non-implant contact',
    medUse: 'Surgical instruments, trays, enclosures, non-implant hardware',
  },
  {
    name: '316L Stainless Steel',
    category: 'Metal',
    yieldMPa: '170',
    ultimateMPa: '485',
    shearMPa: '290',
    hardness: 'max 95 HRB / 217 HB',
    density: '8.00 g/cm³',
    h2o2Compat: 'Excellent',
    h2o2Note: 'Mo content improves pitting resistance over 304',
    gammaCap: 'Excellent',
    gammaNote: 'No degradation; preferred for implant sterilisation hardware',
    autoclave: 'Yes',
    autoclaveNote: 'Excellent—primary implant & instrument material',
    biocompat: 'ISO 5832-1; ASTM F138 for implants',
    medUse: 'Bone screws, orthopedic implants, surgical instruments, stents',
  },
  {
    name: 'Titanium Grade 2 (CP)',
    category: 'Metal',
    yieldMPa: '340',
    ultimateMPa: '430',
    shearMPa: '380',
    hardness: '98 HRB',
    density: '4.51 g/cm³',
    h2o2Compat: 'Excellent',
    h2o2Note: 'TiO₂ passive layer highly resistant to H₂O₂',
    gammaCap: 'Excellent',
    gammaNote: 'Virtually unaffected; used in MRI-compatible implants',
    autoclave: 'Yes',
    autoclaveNote: 'Excellent; oxide layer stable at 134 °C steam',
    biocompat: 'ASTM F67; ISO 5832-2; exceptional osseointegration',
    medUse: 'Dental implants, bone plates, cardiac pacemaker housings',
  },
  {
    name: 'Titanium Ti-6Al-4V ELI',
    category: 'Metal',
    yieldMPa: '880',
    ultimateMPa: '950',
    shearMPa: '550',
    hardness: '36 HRC (334 HB)',
    density: '4.43 g/cm³',
    h2o2Compat: 'Excellent',
    h2o2Note: 'TiO₂ passive layer; no measurable corrosion in clinical H₂O₂',
    gammaCap: 'Excellent',
    gammaNote: 'No degradation; ASTM F136 approved for implants',
    autoclave: 'Yes',
    autoclaveNote: 'Excellent; preferred for long-term implantable devices',
    biocompat: 'ASTM F136; ISO 5832-3; gold standard implant alloy',
    medUse: 'Hip/knee implants, spinal cages, trauma fixation, surgical tools',
  },
  {
    name: 'Cobalt-Chrome (ASTM F75)',
    category: 'Metal',
    yieldMPa: '480–840',
    ultimateMPa: '780–1280',
    shearMPa: '490',
    hardness: '25–35 HRC',
    density: '8.40 g/cm³',
    h2o2Compat: 'Excellent',
    h2o2Note: 'Passive Cr₂O₃ layer; excellent oxidative resistance',
    gammaCap: 'Excellent',
    gammaNote: 'No degradation; used in radiation beam limiting devices',
    autoclave: 'Yes',
    autoclaveNote: 'Excellent; used in reusable orthopedic instruments',
    biocompat: 'ASTM F75/F799; ISO 5832-4; major implant alloy',
    medUse: 'Total joint replacement, dental prosthetics, cardiovascular stents',
  },
  {
    name: 'Nitinol (55-Ni, 45-Ti)',
    category: 'Metal',
    yieldMPa: '195–690',
    ultimateMPa: '895',
    shearMPa: '~350',
    hardness: '30–35 HRC',
    density: '6.45 g/cm³',
    h2o2Compat: 'Good',
    h2o2Note: 'TiO₂/NiO passive layer; avoid concentrated H₂O₂',
    gammaCap: 'Good',
    gammaNote: 'Shape memory not significantly impaired; verify per lot',
    autoclave: 'Conditional',
    autoclaveNote: 'Steam autoclave accepted; cycle count may affect fatigue life',
    biocompat: 'ASTM F2063; ISO 10993; FDA-cleared devices exist',
    medUse: 'Self-expanding stents, guidewires, orthodontic wires, filters',
  },
  // ── POLYMERS ──
  {
    name: 'PEEK (Poly-ether-ether-ketone)',
    category: 'Polymer',
    yieldMPa: '98',
    ultimateMPa: '98',
    shearMPa: '53',
    hardness: '84.5 Shore D',
    density: '1.30 g/cm³',
    h2o2Compat: 'Excellent',
    h2o2Note: 'Highly resistant to H₂O₂ at all clinical concentrations',
    gammaCap: 'Good',
    gammaNote: 'Slight yellowing >25 kGy; mechanical properties retained ≤50 kGy',
    autoclave: 'Yes',
    autoclaveNote: 'Stable to 250 °C; repeated autoclave cycles well tolerated',
    biocompat: 'ISO 10993; ASTM F2026; FDA-cleared implant-grade available',
    medUse: 'Spinal cages, trauma implants, instrument handles, catheter hubs',
  },
  {
    name: 'PTFE (Polytetrafluoroethylene)',
    category: 'Polymer',
    yieldMPa: '21–35',
    ultimateMPa: '21–35',
    shearMPa: '~14',
    hardness: '50–65 Shore D',
    density: '2.15–2.20 g/cm³',
    h2o2Compat: 'Excellent',
    h2o2Note: 'Chemically inert; unaffected by any concentration of H₂O₂',
    gammaCap: 'Limited',
    gammaNote: 'Chain scission causes embrittlement above ~10 kGy; not recommended for gamma sterilisation',
    autoclave: 'Yes',
    autoclaveNote: 'Melting point 327 °C; steam autoclave well tolerated',
    biocompat: 'USP Class VI; ISO 10993; long implant history (ePTFE vascular grafts)',
    medUse: 'Tubing, vascular grafts, catheter liners, seals, gaskets',
  },
  {
    name: 'Polysulfone (PSU)',
    category: 'Polymer',
    yieldMPa: '70',
    ultimateMPa: '70',
    shearMPa: '48',
    hardness: '~120 Rockwell M',
    density: '1.24 g/cm³',
    h2o2Compat: 'Good',
    h2o2Note: 'Resistant to dilute H₂O₂; avoid >30% concentration',
    gammaCap: 'Moderate',
    gammaNote: 'Yellowing and embrittlement >25 kGy; use EtO or steam instead',
    autoclave: 'Yes',
    autoclaveNote: 'Rated to 160 °C; repeated autoclaving supported (Udel P-1700)',
    biocompat: 'USP Class VI; ISO 10993; used in dialysis membranes',
    medUse: 'Surgical instrument housings, dialysis membranes, fluid management',
  },
  {
    name: 'Nylon 12 (PA 12)',
    category: 'Polymer',
    yieldMPa: '50',
    ultimateMPa: '50',
    shearMPa: '35',
    hardness: '78 Shore D',
    density: '1.02 g/cm³',
    h2o2Compat: 'Good',
    h2o2Note: 'Resistant to dilute H₂O₂; prolonged exposure may affect surface',
    gammaCap: 'Moderate',
    gammaNote: 'Discoloration and minor property loss above 25 kGy; acceptable for single-use',
    autoclave: 'Limited',
    autoclaveNote: 'Softens near 180 °C; limited autoclave cycles; moisture absorption risk',
    biocompat: 'ISO 10993 tested; used in single-use devices',
    medUse: 'Catheter shafts, tubing, connectors, balloon catheters',
  },
  {
    name: 'Nylon 6/6 (PA 66)',
    category: 'Polymer',
    yieldMPa: '90',
    ultimateMPa: '90',
    shearMPa: '69',
    hardness: '80 Shore D / 88 Rockwell M',
    density: '1.14 g/cm³',
    h2o2Compat: 'Moderate',
    h2o2Note: 'Acceptable short-term; H₂O₂ oxidation can degrade amide bonds over time',
    gammaCap: 'Moderate',
    gammaNote: 'Yellowing and reduced elongation above 25 kGy',
    autoclave: 'Limited',
    autoclaveNote: 'High moisture absorption; dimension change after autoclave cycles',
    biocompat: 'USP Class VI grades available; ISO 10993 tested',
    medUse: 'Sutures, instrument handles, cable ties in devices',
  },
  {
    name: 'Polypropylene (PP)',
    category: 'Polymer',
    yieldMPa: '30–33',
    ultimateMPa: '30–33',
    shearMPa: '~20',
    hardness: '92 Rockwell R / 55–65 Shore D',
    density: '0.905 g/cm³',
    h2o2Compat: 'Good',
    h2o2Note: 'Good resistance to H₂O₂; slight surface oxidation at >30%',
    gammaCap: 'Limited',
    gammaNote: 'Significant embrittlement without antioxidant additives; use gamma-stabilised grades',
    autoclave: 'Conditional',
    autoclaveNote: 'Virgin PP marginal at 121 °C (softening ~160 °C); autoclavable grades available',
    biocompat: 'USP Class VI; widely used in medical packaging and devices',
    medUse: 'Syringes, IV containers, surgical mesh, device packaging',
  },
  {
    name: 'Polycarbonate (PC)',
    category: 'Polymer',
    yieldMPa: '62',
    ultimateMPa: '66',
    shearMPa: '41',
    hardness: 'R118 / M70 Rockwell',
    density: '1.20 g/cm³',
    h2o2Compat: 'Moderate',
    h2o2Note: 'Susceptible to stress cracking with concentrated H₂O₂; dilute acceptable',
    gammaCap: 'Good',
    gammaNote: 'Yellowing but properties largely retained ≤25 kGy; gamma-stabilised grades exist',
    autoclave: 'Limited',
    autoclaveNote: 'Prone to hydrolysis and optical hazing; limited steam cycles; EtO preferred',
    biocompat: 'ISO 10993; USP Class VI; widely used for transparent device components',
    medUse: 'Blood oxygenator housings, surgical helmets, IV connectors, scopes',
  },
  {
    name: 'PLA (Polylactic Acid)',
    category: 'Polymer',
    yieldMPa: '60',
    ultimateMPa: '53',
    shearMPa: '~36',
    hardness: '83 Shore D',
    density: '1.24 g/cm³',
    h2o2Compat: 'Poor',
    h2o2Note: 'H₂O₂ accelerates hydrolytic degradation; not suitable for repeated exposure',
    gammaCap: 'Moderate',
    gammaNote: 'Molecular weight loss at >15 kGy; used for absorbable implants where controlled degradation is desired',
    autoclave: 'No',
    autoclaveNote: 'Glass transition ~60 °C; melts/deforms completely at autoclave temperatures',
    biocompat: 'FDA cleared for absorbable devices; ISO 10993; GRAS status for some grades',
    medUse: 'Absorbable sutures, resorbable bone screws, 3D-printed anatomical models',
  },
  {
    name: 'UHMWPE',
    category: 'Polymer',
    yieldMPa: '21–28',
    ultimateMPa: '27–46',
    shearMPa: '~20',
    hardness: '60–70 Shore D',
    density: '0.93–0.94 g/cm³',
    h2o2Compat: 'Good',
    h2o2Note: 'Chemically resistant; minimal effect from H₂O₂',
    gammaCap: 'Conditional',
    gammaNote: 'Gamma in air causes oxidative embrittlement; sterilise in vacuum/inert atmosphere',
    autoclave: 'Conditional',
    autoclaveNote: 'Melts ~135 °C; steam sterilisation not ideal; EtO or gamma (inert) preferred',
    biocompat: 'ASTM F648; ISO 5834; gold standard for bearing surfaces',
    medUse: 'Total hip/knee bearing liners, spine disc replacements, sutures',
  },
  {
    name: 'Silicone (Medical Grade)',
    category: 'Polymer',
    yieldMPa: '8–11',
    ultimateMPa: '8–11',
    shearMPa: '~4',
    hardness: '35–80 Shore A',
    density: '1.11–1.20 g/cm³',
    h2o2Compat: 'Excellent',
    h2o2Note: 'Essentially inert to H₂O₂ at all clinical concentrations',
    gammaCap: 'Good',
    gammaNote: 'Some property change >100 kGy; standard 25 kGy dose well tolerated',
    autoclave: 'Yes',
    autoclaveNote: 'Stable >200 °C; repeated autoclaving is standard practice',
    biocompat: 'ISO 10993; FDA 21 CFR 177.2600; long implant history',
    medUse: 'Tubing, seals, catheters, breast implants, fluid-contact components',
  },
  {
    name: 'ABS (Acrylonitrile Butadiene Styrene)',
    category: 'Polymer',
    yieldMPa: '42',
    ultimateMPa: '42',
    shearMPa: '~28',
    hardness: '102 Rockwell R',
    density: '1.04 g/cm³',
    h2o2Compat: 'Poor',
    h2o2Note: 'Susceptible to surface oxidation and stress cracking; not recommended',
    gammaCap: 'Moderate',
    gammaNote: 'Yellowing and embrittlement above 25 kGy without stabilisers',
    autoclave: 'No',
    autoclaveNote: 'Heat deflection ~98 °C; deforms under autoclave conditions',
    biocompat: 'USP Class VI grades exist; limited direct patient contact',
    medUse: 'Device housings, external enclosures, non-patient-contact components',
  },
  {
    name: 'PETG (Polyethylene Terephthalate Glycol)',
    category: 'Polymer',
    yieldMPa: '50',
    ultimateMPa: '50',
    shearMPa: '~32',
    hardness: '76 Shore D',
    density: '1.27 g/cm³',
    h2o2Compat: 'Good',
    h2o2Note: 'Resistant to dilute H₂O₂ (≤10%); concentrated or prolonged exposure causes surface crazing',
    gammaCap: 'Moderate',
    gammaNote: 'Yellowing above 15 kGy; acceptable for single-use devices at standard 25 kGy with stabilised grades',
    autoclave: 'No',
    autoclaveNote: 'Glass transition ~80 °C; deforms at autoclave temperatures; EtO or VHP preferred',
    biocompat: 'USP Class VI grades available; ISO 10993 tested; used in single-use fluid path components',
    medUse: 'Single-use fluid connectors, IV set components, diagnostic device housings, 3D-printed prototypes',
  },
  {
    name: 'Acetal / POM (Polyoxymethylene)',
    category: 'Polymer',
    yieldMPa: '65–70',
    ultimateMPa: '65–70',
    shearMPa: '~52',
    hardness: '120 Rockwell M',
    density: '1.41–1.42 g/cm³',
    h2o2Compat: 'Moderate',
    h2o2Note: 'POM susceptible to oxidative attack; concentrated H₂O₂ causes surface degradation and formaldehyde off-gassing',
    gammaCap: 'Poor',
    gammaNote: 'Severe degradation above 10 kGy; formaldehyde generation; not recommended for gamma sterilisation',
    autoclave: 'Limited',
    autoclaveNote: 'Melting point ~166 °C (homopolymer) / ~162 °C (copolymer); marginal at 134 °C; EtO preferred',
    biocompat: 'USP Class VI copolymer grades (Delrin, Celcon); ISO 10993 tested; avoid implant use due to formaldehyde risk',
    medUse: 'Instrument handles, gear mechanisms, valve seats, drug delivery device components, pump housings',
  },
  {
    name: 'MPU 100 (Medical Polyurethane)',
    category: 'Polymer',
    yieldMPa: '35–55',
    ultimateMPa: '35–55',
    shearMPa: '~25',
    hardness: '75–95 Shore A',
    density: '1.10–1.20 g/cm³',
    h2o2Compat: 'Good',
    h2o2Note: 'Medical-grade PU formulations show good resistance to dilute H₂O₂; avoid high concentrations',
    gammaCap: 'Good',
    gammaNote: 'Gamma-stabilised formulations retain properties at 25 kGy; minor discolouration; verify per specific grade',
    autoclave: 'No',
    autoclaveNote: 'Thermally sensitive; hydrolysis risk at elevated temperature/humidity; EtO or VHP preferred',
    biocompat: 'ISO 10993; FDA Master File eligible; MPU 100 formulated for long-term implant contact per ISO 10993-6',
    medUse: 'Catheter shafts, pacemaker lead insulation, wound contact films, long-term implantable tubing',
  },
];

// ── Big comparison table ────────────────────────────────────────────────────
function MaterialsTable() {
  const thBase = {
    background: '#1e7a36', color: '#fff', padding: '9px 10px',
    textAlign: 'center', fontWeight: '700', fontSize: '0.8rem',
    border: '1px solid #145a24', whiteSpace: 'nowrap',
  };
  const thLeft = { ...thBase, textAlign: 'left' };
  const tdBase = {
    padding: '8px 10px', border: '1px solid #b8dfc2', verticalAlign: 'middle',
    fontSize: '0.82rem', textAlign: 'center',
  };
  const tdLeft = { ...tdBase, textAlign: 'left', fontWeight: '600' };

  const metals = MATERIALS.filter(m => m.category === 'Metal');
  const polymers = MATERIALS.filter(m => m.category === 'Polymer');

  function Row({ m, idx }) {
    const bg = idx % 2 === 0 ? '#e8f5ec' : '#f5faf7';
    const td = { ...tdBase, background: bg };
    const tdL = { ...tdLeft, background: bg };
    return (
      <tr>
        <td style={{ ...tdL, whiteSpace: 'nowrap' }}>{m.name}</td>
        <td style={td}>{m.yieldMPa}</td>
        <td style={td}>{m.ultimateMPa}</td>
        <td style={td}>{m.shearMPa}</td>
        <td style={td}>{m.hardness}</td>
        <td style={td}>{m.density}</td>
        <td style={{ ...td, padding: '6px 8px' }}>
          <RatingBadge value={m.h2o2Compat} />
          <div style={{ fontSize: '0.74rem', color: '#444', marginTop: '3px', textAlign: 'left', lineHeight: '1.3' }}>{m.h2o2Note}</div>
        </td>
        <td style={{ ...td, padding: '6px 8px' }}>
          <RatingBadge value={m.gammaCap} />
          <div style={{ fontSize: '0.74rem', color: '#444', marginTop: '3px', textAlign: 'left', lineHeight: '1.3' }}>{m.gammaNote}</div>
        </td>
        <td style={{ ...td, padding: '6px 8px' }}>
          <RatingBadge value={m.autoclave} />
          <div style={{ fontSize: '0.74rem', color: '#444', marginTop: '3px', textAlign: 'left', lineHeight: '1.3' }}>{m.autoclaveNote}</div>
        </td>
      </tr>
    );
  }

  function SectionHeader({ label, colspan }) {
    return (
      <tr>
        <td colSpan={colspan} style={{
          background: '#145a24', color: '#c8f0d0', fontWeight: '700',
          fontSize: '0.88rem', padding: '7px 12px', letterSpacing: '0.06em',
          textTransform: 'uppercase', border: '1px solid #0e4020',
        }}>{label}</td>
      </tr>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem', minWidth: '1000px' }}>
        <thead>
          <tr>
            <th style={{ ...thLeft, width: '175px' }}>Material</th>
            <th style={thBase}>Yield Strength<br />(MPa)</th>
            <th style={thBase}>UTS<br />(MPa)</th>
            <th style={thBase}>Shear Strength<br />(MPa)</th>
            <th style={thBase}>Hardness</th>
            <th style={thBase}>Density</th>
            <th style={{ ...thBase, width: '195px' }}>H₂O₂ Compatibility</th>
            <th style={{ ...thBase, width: '195px' }}>Gamma Radiation</th>
            <th style={{ ...thBase, width: '195px' }}>Autoclave</th>
          </tr>
        </thead>
        <tbody>
          <SectionHeader label="Metals & Alloys" colspan={9} />
          {metals.map((m, i) => <Row key={m.name} m={m} idx={i} />)}
          <SectionHeader label="Polymers & Thermoplastics" colspan={9} />
          {polymers.map((m, i) => <Row key={m.name} m={m} idx={i} />)}
        </tbody>
      </table>
    </div>
  );
}

// ── Detail cards for biocompatibility & use cases ───────────────────────────
function DetailTable() {
  const thBase = {
    background: '#2a7a42', color: '#fff', padding: '9px 12px',
    textAlign: 'left', fontWeight: '700', fontSize: '0.86rem',
    border: '1px solid #1a5a2a',
  };
  const tdBase = {
    padding: '8px 12px', border: '1px solid #b8dfc2', verticalAlign: 'top',
    fontSize: '0.84rem',
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
        <thead>
          <tr>
            <th style={{ ...thBase, width: '175px' }}>Material</th>
            <th style={thBase}>Biocompatibility Standard</th>
            <th style={thBase}>Primary Medical Applications</th>
          </tr>
        </thead>
        <tbody>
          {MATERIALS.map((m, i) => (
            <tr key={m.name}>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontWeight: '600', color: '#145a24' }}>{m.name}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontFamily: "'Courier New', monospace", fontSize: '0.82rem' }}>{m.biocompat}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7' }}>{m.medUse}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Sterilisation comparison matrix ────────────────────────────────────────
const STERIL_DATA = [
  { method: 'Steam Autoclave (121 °C)', desc: '15 min @ 121 °C / 15 psi; or 3 min @ 134 °C', best: 'Metals, PEEK, silicone, PSU', avoid: 'PLA, ABS, PC, PP (standard), Nylon', note: 'SAL 10⁻⁶ achievable; no residuals; cost-effective' },
  { method: 'Gamma Irradiation', desc: '25–50 kGy (ISO 11137); Co-60 or e-beam', best: 'Metals, PEEK, silicone, PC (stabilised)', avoid: 'PTFE, UHMWPE (in air), PLA (high dose)', note: 'Penetrates sealed packaging; can cause polymer degradation' },
  { method: 'Ethylene Oxide (EtO)', desc: '37–63 °C, 30–60% RH; aeration required', best: 'All polymers, temperature-sensitive devices', avoid: 'Devices with EtO-trapping materials', note: 'Residual EtO toxicity; 12–24 h aeration required; FDA 21 CFR 820' },
  { method: 'Hydrogen Peroxide Plasma (VHP)', desc: '45–55 °C, H₂O₂ vapor 6 mg/L; ISO 22441', best: 'PEEK, metals, silicone, polysulfone', avoid: 'Cellulose materials, paper; liquids; lumens >1 m', note: 'No toxic residuals; compatible with most metals and semi-crystalline polymers' },
  { method: 'Dry Heat (160–180 °C)', desc: '2 h @ 160 °C or 1 h @ 170 °C', best: 'Metals, glass, ceramics', avoid: 'Almost all polymers; rubber', note: 'Used for oil-based instruments; less common in modern practice' },
];

function SterilisationTable() {
  const thBase = {
    background: '#1e7a36', color: '#fff', padding: '9px 12px',
    textAlign: 'left', fontWeight: '700', fontSize: '0.86rem',
    border: '1px solid #145a24',
  };
  const tdBase = {
    padding: '8px 12px', border: '1px solid #b8dfc2', verticalAlign: 'top',
    fontSize: '0.84rem',
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
        <thead>
          <tr>
            <th style={{ ...thBase, width: '200px' }}>Method</th>
            <th style={thBase}>Conditions</th>
            <th style={thBase}>Best For</th>
            <th style={thBase}>Avoid With</th>
            <th style={thBase}>Key Note</th>
          </tr>
        </thead>
        <tbody>
          {STERIL_DATA.map((s, i) => (
            <tr key={s.method}>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontWeight: '700', color: '#145a24' }}>{s.method}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontFamily: "'Courier New', monospace", fontSize: '0.81rem' }}>{s.desc}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', color: '#1a5c1a' }}>{s.best}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', color: '#7a2020' }}>{s.avoid}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7' }}>{s.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main export ─────────────────────────────────────────────────────────────
export default function MedicalMaterials() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Medical Device Materials Reference</h1>
      <div style={S.subtitle}>
        Mechanical properties, sterilisation compatibility, chemical resistance, and biocompatibility for metals and polymers common in Class II/III medical devices.
        Values represent typical/representative data; always verify against material lot certification and applicable ASTM/ISO standards.
      </div>

      {/* Legend */}
      <div style={S.legendBox}>
        <strong style={{ fontSize: '0.85rem', color: '#145a24', alignSelf: 'center' }}>Rating Key:</strong>
        {[
          ['Excellent', RATING_COLORS['Excellent']],
          ['Good', RATING_COLORS['Good']],
          ['Moderate', RATING_COLORS['Moderate']],
          ['Limited', RATING_COLORS['Limited']],
          ['Poor', RATING_COLORS['Poor']],
          ['Yes', RATING_COLORS['Yes']],
          ['Conditional', RATING_COLORS['Conditional']],
          ['No', RATING_COLORS['No']],
        ].map(([label, c]) => (
          <div key={label} style={S.legendItem}>
            <div style={{ ...S.legendDot, background: c.bg, border: `1px solid ${c.border}` }} />
            <span style={{ color: c.text, fontWeight: '600' }}>{label}</span>
          </div>
        ))}
      </div>

      <h2 style={S.h2}>Mechanical Properties & Sterilisation Compatibility Matrix</h2>
      <p style={S.p}>
        Yield strength (0.2% offset), ultimate tensile strength (UTS), shear strength, hardness, and density are
        tabulated alongside compatibility ratings for three primary sterilisation modalities used in medical device
        manufacturing: hydrogen peroxide vapor (VHP/plasma), gamma irradiation, and steam autoclave.
      </p>
      <div style={S.noteBox}>
        ⚠ <strong>UTS vs Yield:</strong> For metals, yield and UTS differ significantly. For most polymers, the
        "yield" point is near fracture (brittle) or the material does not exhibit a distinct yield point; UTS is
        listed in both columns where they are effectively the same. Shear strength ≈ 0.6–0.75 × UTS for metals
        (von Mises); polymer shear values are empirical.
      </div>
      <MaterialsTable />

      <h2 style={S.h2}>Biocompatibility Standards & Primary Applications</h2>
      <p style={S.p}>
        ISO 10993 is the primary framework for biological evaluation of medical devices. Key tests include
        cytotoxicity (10993-5), sensitisation (10993-10), genotoxicity (10993-3), and implantation studies
        (10993-6). ASTM standards govern alloy composition for implantable metals.
      </p>
      <DetailTable />

      <h2 style={S.h2}>Sterilisation Method Compatibility Overview</h2>
      <p style={S.p}>
        Sterilisation method selection is driven by material compatibility, device geometry (lumens, coatings),
        regulatory requirements, and SAL (Sterility Assurance Level) requirements. ISO 11135 (EtO), ISO 11137
        (radiation), ISO 17665 (steam), and ISO 22441 (VHP) govern validation requirements.
      </p>
      <SterilisationTable />

      <h2 style={S.h2}>Key Properties: Definitions & Test Methods</h2>
      <Table2 rows={[
        ['Yield Strength', 'Stress at 0.2% plastic strain offset (ASTM E8/E8M for metals; ISO 527 for polymers)\nThe design limit for structural components — operation above this causes permanent deformation'],
        ['Ultimate Tensile Strength (UTS)', 'Maximum stress a material withstands before fracture (ASTM E8; ISO 527)\nUse with safety factors (typically 2–4×) for implant load-bearing design'],
        ['Shear Strength', 'Maximum shear stress before failure (ASTM B831; ASTM D732 for plastics)\nCritical for fasteners, bone screws, and shear-loaded implant interfaces'],
        ['Hardness', 'Resistance to localised plastic deformation\nBrinell (HB): metals, low to medium hardness; Rockwell HRC: hardened metals;\nRockwell M/R: rigid polymers; Shore D: semi-rigid polymers; Shore A: elastomers'],
        ['Density / Specific Mass', 'Mass per unit volume (g/cm³ or kg/m³)\nDrives implant weight; MRI artifact size proportional to density for metals'],
        ['H₂O₂ Compatibility', 'Resistance to 6–30% vaporized or liquid hydrogen peroxide at 45–55 °C\nPrimary sporicidal agent in VHP (Bioquell, STERRAD) sterilisation systems; also used for HLD'],
        ['Gamma Radiation Capability', 'Resistance to 25–50 kGy Co-60 gamma or e-beam irradiation (ISO 11137-1)\nHigh-energy photons cause chain scission in polymers and activation in some metals'],
        ['Autoclave Compatibility', 'Resistance to saturated steam: 121 °C/15 psi/15 min or 134 °C/30 psi/3 min (ISO 17665)\nMost critical for reusable surgical instruments; tests hydrolytic stability'],
      ]} />

      <h2 style={S.h2}>Design Selection Guidance</h2>
      <InfoBox title="Metal Selection Decision Tree" items={[
        'Non-implant, reusable instruments → 316L SS (corrosion + autoclave) or 304 SS (cost)',
        'Load-bearing implants (hip, knee, spine) → Ti-6Al-4V ELI or CoCr F75',
        'Dental implants / osseointegration required → Ti Grade 2 (CP) or Ti-6Al-4V ELI',
        'Cardiovascular / stent (self-expanding) → Nitinol; permanent stent → CoCr or 316L',
        'Weight-sensitive, non-implant structures → Al 6061-T6 (anodized for corrosion)',
        'MRI compatibility required → Titanium grades or 316L (verify susceptibility per ASTM F2503)',
      ]} />
      <InfoBox title="Polymer Selection Decision Tree" items={[
        'High-performance implantable or reusable → PEEK (best all-round sterilisation compatibility)',
        'Chemically inert tubing / seals / low-load → PTFE or Medical-Grade Silicone',
        'Transparent housings / fluid path → Polycarbonate (EtO preferred) or Polysulfone (autoclave)',
        'Single-use catheter shafts / connectors → PA 12 (Nylon 12) or PA 66',
        'Absorbable / biodegradable application → PLA or PLGA; no autoclave, no H₂O₂',
        'Bearing surfaces (joint replacement) → UHMWPE (sterilise in nitrogen/vacuum, not air gamma)',
        'Flexible seals, patient contact → Medical-grade silicone (broadest compatibility)',
        'Avoid for patient-contact: ABS, standard PP without gamma stabilisers',
      ]} />

      <h2 style={S.h2}>Regulatory & Standards Reference</h2>
      <Table2 rows={[
        ['ISO 10993 Series', 'Biological Evaluation of Medical Devices — 23 parts covering cytotoxicity, genotoxicity,\nimplantation, sensitisation, pyrogenicity, hemocompatibility, and systemic toxicity'],
        ['ASTM F136', 'Standard Specification for Wrought Ti-6Al-4V ELI for surgical implant applications'],
        ['ASTM F138', 'Standard Specification for 316L SS bar/wire for surgical implants (UNS S31673)'],
        ['ASTM F75', 'Standard Specification for CoCrMo Alloy for casting and powder metallurgy (implants)'],
        ['ASTM F2026', 'Standard Specification for PEEK polymers for surgical implant applications'],
        ['ASTM F648', 'Standard Specification for UHMWPE powder and fabricated forms for surgical implants'],
        ['ISO 5832 Series', 'Implants for surgery — Metallic materials: Parts 1–14 cover SS, Ti, CoCr, Ta, Nb alloys'],
        ['ISO 11137-1/2/3', 'Sterilization of health-care products — Radiation: validation, dose setting, guidance'],
        ['ISO 17665-1', 'Sterilization of health-care products — Moist heat: requirements for medical devices'],
        ['ISO 11135', 'Sterilization of health-care products — Ethylene oxide: requirements for dev/validation'],
        ['ISO 22441', 'Sterilization of health-care products — Hydrogen peroxide (VHP) — requirements'],
        ['FDA 21 CFR Part 820', 'Quality System Regulation (QSR) / Device Manufacturing Requirements'],
        ['FDA 510(k) / PMA', '510(k) for substantially equivalent devices; PMA for Class III implants'],
      ]} />

      <InfoBox title="Important Caveats" items={[
        'All mechanical values are representative/typical at room temperature for standard forms. Grade, heat treatment, surface finish, and manufacturing process significantly affect properties.',
        'Chemical compatibility ratings assume standard clinical use concentrations and contact times. Extended immersion, elevated temperature, or mechanical stress will reduce compatibility.',
        'Gamma irradiation values assume typical single sterilisation dose (25 kGy). Repeat sterilisation and dose escalation cause cumulative degradation in polymers.',
        'Biocompatibility is device- and application-specific. ISO 10993 evaluation must be performed for each specific application, contact duration, and contact type.',
        'Autoclave cycle count matters: all "Yes" ratings assume a reasonable number of reprocessing cycles per validated limit. Cycle counts exceeding 50–200 may show cumulative fatigue/degradation.',
        'Sources: ASM/Matweb datasheets (Al 6061-T6, Ti Grade 2, Ti-6Al-4V annealed); Nedal EN 755-2 datasheet (Al 6082-T6); Penn Stainless / theworldmaterial (316L annealed per ASTM A240); makeitfrom.com ASTM F75 (CoCr); Victrex TDS 450G March 2026 (PEEK); Zeus Inc. / Wefapress (PTFE); Solvay Udel P-1700 TDS (PSU); Smith Metal Centres datasheets (PA12, ABS); Mitsubishi MCAM Ertalon 66SA datasheet (PA66); INEOS / Roncelli (PP); Covestro Makrolon GP / TAP Plastics datasheet (PC); NatureWorks Ingeo 4043D TDS (PLA); NuSil Implant Line Product Guide (Medical Silicone); ASTM F648 / FDA guidance (UHMWPE). Mechanical values are typical at 23°C dry-as-moulded unless noted.',
      ]} />
    </div>
  );
}